import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import {
  CloudUploadOutlinedIcon,
  DeleteOutlineIcon,
  DownloadOutlinedIcon,
  FileOutlinedIcon,
} from "../../utilities/icons";
import {
  employeeDocumentsApi,
  employeeDocumentDeleteApi,
  employeeDocumentDownloadApi,
} from "../../apiCalls/Apicalls";

const CATEGORIES = [
  { value: "resume", label: "Resume / CV" },
  { value: "photo", label: "Photo" },
  { value: "id_proof", label: "ID Proof" },
  { value: "certificate", label: "Certificate" },
  { value: "other", label: "Other" },
];

const CATEGORY_COLORS = {
  resume: { bg: "rgba(65,139,255,0.1)", color: "#418BFF" },
  photo: { bg: "rgba(34,197,94,0.1)", color: "#16a34a" },
  id_proof: { bg: "rgba(251,146,60,0.1)", color: "#ea580c" },
  certificate: { bg: "rgba(168,85,247,0.1)", color: "#9333ea" },
  other: { bg: "rgba(100,116,139,0.1)", color: "#475569" },
};

const ALLOWED_TYPES = ".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx";
const MAX_SIZE_MB = 10;

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function DocRow({ doc, onDelete, onDownload }) {
  const catStyle = CATEGORY_COLORS[doc.category] || CATEGORY_COLORS.other;
  const catLabel = CATEGORIES.find((c) => c.value === doc.category)?.label || doc.category;
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    await onDownload(doc);
    setDownloading(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    await onDelete(doc._id);
    setDeleting(false);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      px={2}
      py={1.5}
      sx={{
        borderBottom: "1px solid var(--ax-border)",
        "&:last-child": { borderBottom: "none" },
        "&:hover": { bgcolor: "var(--ax-bg)" },
        transition: "background 120ms ease",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" flex={1} minWidth={0}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            bgcolor: catStyle.bg,
            color: catStyle.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FileOutlinedIcon fontSize="small" />
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "0.84rem",
              color: "var(--ax-ink)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {doc.originalFilename}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" mt={0.25}>
            <Chip
              label={catLabel}
              size="small"
              sx={{
                bgcolor: catStyle.bg,
                color: catStyle.color,
                fontWeight: 500,
                fontSize: "0.68rem",
                height: 18,
                borderRadius: "4px",
              }}
            />
            <Typography sx={{ fontSize: "0.72rem", color: "var(--ax-ink-2)" }}>
              {formatBytes(doc.size)} · {formatDate(doc.uploadedAt)}
            </Typography>
          </Stack>
          {doc.description && (
            <Typography
              sx={{
                fontSize: "0.74rem",
                color: "var(--ax-ink-2)",
                mt: 0.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {doc.description}
            </Typography>
          )}
        </Box>
      </Stack>

      <Stack direction="row" spacing={0.5} flexShrink={0} ml={1}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleDownload}
          disabled={downloading || deleting}
          sx={{
            minWidth: 32,
            width: 32,
            height: 32,
            p: 0,
            borderColor: "var(--ax-border)",
            color: "var(--ax-ink-2)",
            "&:hover": { borderColor: "var(--ax-blue)", color: "var(--ax-blue)" },
          }}
        >
          {downloading ? <CircularProgress size={14} /> : <DownloadOutlinedIcon fontSize="small" />}
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={handleDelete}
          disabled={deleting || downloading}
          sx={{
            minWidth: 32,
            width: 32,
            height: 32,
            p: 0,
            borderColor: "var(--ax-border)",
            color: "var(--ax-ink-2)",
            "&:hover": { borderColor: "var(--ax-red)", color: "var(--ax-red)" },
          }}
        >
          {deleting ? <CircularProgress size={14} /> : <DeleteOutlineIcon fontSize="small" />}
        </Button>
      </Stack>
    </Stack>
  );
}

function EmployeeDocuments({ empId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("other");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const authHeaders = () => ({
    headers: { Authorization: Cookies.get("jwtToken") },
  });

  const fetchDocuments = useCallback(() => {
    if (!empId) return;
    setLoading(true);
    axios
      .get(employeeDocumentsApi(empId), authHeaders())
      .then((res) => setDocs(res.data || []))
      .catch(() => toast.error("Failed to load documents"))
      .finally(() => setLoading(false));
  }, [empId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`File must be ${MAX_SIZE_MB} MB or smaller`);
      e.target.value = "";
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("category", category);
    formData.append("description", description.trim());

    try {
      await axios.post(employeeDocumentsApi(empId), formData, {
        headers: {
          Authorization: Cookies.get("jwtToken"),
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Document uploaded successfully");
      setSelectedFile(null);
      setDescription("");
      setCategory("other");
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchDocuments();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(
        employeeDocumentDownloadApi(empId, doc._id),
        authHeaders()
      );
      const url = res.data?.downloadUrl;
      if (url) {
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.originalFilename;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        toast.error("Download link unavailable");
      }
    } catch {
      toast.error("Failed to get download link");
    }
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(employeeDocumentDeleteApi(empId, docId), authHeaders());
      toast.success("Document deleted");
      setDocs((prev) => prev.filter((d) => d._id !== docId));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  return (
    <Box>
      {/* Upload Panel */}
      <Box
        sx={{
          bgcolor: "var(--ax-surface)",
          border: "1px solid var(--ax-border)",
          borderRadius: "var(--ax-radius-lg)",
          p: 2.5,
          mb: 2,
        }}
      >
        <Typography
          sx={{ fontWeight: 600, fontSize: "0.84rem", color: "var(--ax-ink)", mb: 1.5 }}
        >
          Upload Document
        </Typography>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                label="Category"
                disabled={uploading}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={uploading}
              inputProps={{ maxLength: 200 }}
              fullWidth
            />
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              component="label"
              disabled={uploading}
              startIcon={<CloudUploadOutlinedIcon fontSize="small" />}
              sx={{
                borderColor: "var(--ax-border)",
                color: "var(--ax-ink-2)",
                textTransform: "none",
                fontSize: "0.8rem",
                "&:hover": { borderColor: "var(--ax-blue)", color: "var(--ax-blue)" },
              }}
            >
              Choose File
              <input
                ref={fileInputRef}
                hidden
                type="file"
                accept={ALLOWED_TYPES}
                onChange={handleFileChange}
              />
            </Button>
            {selectedFile && (
              <Typography sx={{ fontSize: "0.8rem", color: "var(--ax-ink-2)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {selectedFile.name} ({formatBytes(selectedFile.size)})
              </Typography>
            )}
            {selectedFile && (
              <Button
                variant="contained"
                size="small"
                onClick={handleUpload}
                disabled={uploading}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8rem",
                  bgcolor: "var(--ax-blue)",
                  "&:hover": { bgcolor: "var(--ax-blue-dark, #2563eb)" },
                  flexShrink: 0,
                }}
              >
                {uploading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Upload"}
              </Button>
            )}
          </Stack>
          <Typography sx={{ fontSize: "0.72rem", color: "var(--ax-ink-2)" }}>
            Allowed: PDF, JPEG, PNG, WebP, DOC, DOCX · Max {MAX_SIZE_MB} MB
          </Typography>
        </Stack>
      </Box>

      {/* Document List */}
      <Box
        sx={{
          bgcolor: "var(--ax-surface)",
          border: "1px solid var(--ax-border)",
          borderRadius: "var(--ax-radius-lg)",
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          px={2}
          py={1.5}
          borderBottom="1px solid var(--ax-border)"
        >
          <Typography sx={{ fontWeight: 600, fontSize: "0.84rem", color: "var(--ax-ink)" }}>
            Uploaded Documents
          </Typography>
          <Chip
            label={docs.length}
            size="small"
            sx={{ bgcolor: "var(--ax-bg)", color: "var(--ax-ink-2)", fontWeight: 500, fontSize: "0.72rem" }}
          />
        </Stack>

        {loading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={24} sx={{ color: "var(--ax-blue)" }} />
          </Stack>
        ) : docs.length === 0 ? (
          <Stack alignItems="center" py={4} spacing={0.5}>
            <FileOutlinedIcon fontSize="large" sx={{ color: "var(--ax-ink-3, var(--ax-border))" }} />
            <Typography sx={{ fontSize: "0.84rem", color: "var(--ax-ink-2)" }}>
              No documents uploaded yet
            </Typography>
          </Stack>
        ) : (
          docs.map((doc) => (
            <DocRow
              key={doc._id}
              doc={doc}
              onDelete={handleDelete}
              onDownload={handleDownload}
            />
          ))
        )}
      </Box>
    </Box>
  );
}

export default EmployeeDocuments;
